import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Check } from "lucide-react";

export default function NewsletterSignup({ variant = "band" }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    setMessage("");
    try {
      const { data } = await api.post("/newsletter/subscribe", { email });
      setStatus("done");
      setMessage(
        data.already_subscribed
          ? "You're already on the list — we'll see you in your inbox."
          : "You're in. Watch your inbox for the next dispatch."
      );
      setEmail("");
    } catch (err) {
      setStatus("error");
      setMessage(formatApiError(err.response?.data?.detail) || "Couldn't subscribe — try again?");
    }
  };

  const isLanding = variant === "band";

  return (
    <section
      data-testid="newsletter-signup"
      className={
        isLanding
          ? "border-t border-border bg-[#2C1E16] text-[#F9F6F0]"
          : "border border-border bg-card rounded-sm p-6"
      }
    >
      <div className={isLanding ? "max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-2 gap-10 items-center" : ""}>
        <div>
          <p className={`text-xs uppercase tracking-[0.25em] ${isLanding ? "text-[#F9F6F0]/60" : "text-[#786B63]"}`}>
            The Dispatch
          </p>
          <h2 className={`font-serif mt-3 leading-tight ${isLanding ? "text-4xl sm:text-5xl text-[#F9F6F0]" : "text-2xl"}`}>
            Three good cafés in your inbox, weekly.
          </h2>
          <p className={`mt-3 leading-relaxed max-w-md ${isLanding ? "text-[#F9F6F0]/70" : "text-[#786B63] text-sm"}`}>
            A short weekly note from the BrewFinder editors — one new café, one review worth reading, one bean shop worth a detour. No spam. Unsubscribe anytime.
          </p>
        </div>

        <form onSubmit={submit} className={isLanding ? "md:justify-self-end w-full md:max-w-md" : "mt-4"}>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Mail
                className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isLanding ? "text-[#F9F6F0]/50" : "text-[#786B63]"}`}
                strokeWidth={1.5}
              />
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@morningroutine.com"
                data-testid="newsletter-email-input"
                className={`pl-9 h-11 rounded-sm ${
                  isLanding
                    ? "bg-[#1d130d] border-[#3a2a20] text-[#F9F6F0] placeholder:text-[#F9F6F0]/40 focus-visible:ring-[#C05A46]/50"
                    : "bg-[#F9F6F0] border-border"
                }`}
              />
            </div>
            <Button
              type="submit"
              disabled={status === "loading"}
              data-testid="newsletter-subscribe-btn"
              className="rounded-sm bg-[#C05A46] hover:bg-[#A34A38] text-[#F9F6F0] h-11 px-6"
            >
              {status === "loading" ? "Joining…" : status === "done" ? "Subscribed" : "Join"}
            </Button>
          </div>
          {message && (
            <p
              data-testid="newsletter-message"
              className={`text-xs mt-3 inline-flex items-center gap-1.5 ${
                status === "done"
                  ? isLanding
                    ? "text-[#F9F6F0]/85"
                    : "text-[#2C1E16]"
                  : "text-[#C05A46]"
              }`}
            >
              {status === "done" && <Check size={14} strokeWidth={1.5} />}
              {message}
            </p>
          )}
        </form>
      </div>
    </section>
  );
}
